import React, { createContext, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import useClassRegistrationCheckout from "./useClassRegistrationCheckout";
import useClassCheckoutAnalytics, { postToParent } from "./useClassCheckoutAnalytics";

const ClassRegistrationCheckoutContext = createContext(null);

const ANNOUNCED_PREFIX = "classCheckoutAnnounced:";

const wasAnnounced = (orderNumber) => {
  try {
    return window.sessionStorage.getItem(`${ANNOUNCED_PREFIX}${orderNumber}`) === "1";
  } catch (error) {
    return false;
  }
};

const rememberAnnounced = (orderNumber) => {
  try {
    window.sessionStorage.setItem(`${ANNOUNCED_PREFIX}${orderNumber}`, "1");
  } catch (error) {
    return;
  }
};

export function ClassRegistrationCheckoutProvider({ config, children }) {
  const analytics = useClassCheckoutAnalytics({
    googleTrackingIds: config.googleTrackingIds,
    analyticsItem: config.analyticsItem,
    currencyCode: config.currencyCode,
    pageLocation: config.analyticsPageLocation,
  });
  const checkout = useClassRegistrationCheckout({
    initialQuantity: config.initialQuantity,
    maxQuantity: config.maxQuantity,
    quotes: config.quotes,
    ordersUrl: config.ordersUrl,
    placeOrderUrl: config.placeOrderUrl,
    quotesUrl: config.quotesUrl,
    initialAttendee: config.initialAttendee,
    onOrderPlaced: analytics.beginCheckout,
  });
  const purchasedOrder = checkout.settled === "paid" && checkout.paidInThisBrowser ? checkout.orderStatus : null;

  useEffect(() => {
    if (!purchasedOrder) return;
    analytics.purchase(purchasedOrder);
    if (wasAnnounced(purchasedOrder.order_number)) return;
    rememberAnnounced(purchasedOrder.order_number);
    postToParent("opendateOrderPlaced", {});
  }, [purchasedOrder, analytics.purchase]);

  return (
    <ClassRegistrationCheckoutContext.Provider value={{ ...config, checkout }}>
      {children}
    </ClassRegistrationCheckoutContext.Provider>
  );
}

ClassRegistrationCheckoutProvider.propTypes = {
  config: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
};

export function useClassRegistrationCheckoutContext() {
  const context = useContext(ClassRegistrationCheckoutContext);
  if (!context) throw new Error("useClassRegistrationCheckoutContext must be used inside ClassRegistrationCheckoutProvider");
  return context;
}
